import { azureAdConfig, loginRequest } from '../config/azureAd';
import { clearAppSession } from '../auth-utils';
import { msalInstance } from '../auth/msalInstance';
import { AppUser } from '../auth/mapMsalAccount';

const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect';

interface SessionResponse {
  authenticated: boolean;
  authProvider: string;
  expiry: number;
  user: AppUser;
}

export function storePostLoginRedirect(path: string | null): void {
  if (path) {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
  } else {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  }
}

export function consumePostLoginRedirect(): string | null {
  const redirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return redirect;
}

export async function loginWithMicrosoft(redirectPath?: string | null): Promise<void> {
  // Drop any stale local session so callback cannot skip cookie creation.
  clearAppSession();
  storePostLoginRedirect(redirectPath || null);

  // Clear a stuck MSAL interaction (common after a failed/partial redirect),
  // otherwise loginRedirect throws and the user appears stuck on /login.
  try {
    await msalInstance.handleRedirectPromise({
      navigateToLoginRequestUrl: false,
    });
  } catch {
    // Ignore — we only care about clearing interaction state.
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts[0]) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  await msalInstance.loginRedirect({
    ...loginRequest,
    redirectStartPage: window.location.href,
  });
}

export async function verifyServerSession(): Promise<boolean> {
  try {
    const response = await fetch('/aiapps-auth/me', {
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function createLocalSession(username: string, password: string): Promise<SessionResponse> {
  const response = await fetch('/aiapps-auth/session', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      provider: 'local',
      username,
      password,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || 'Invalid credentials');
  }

  return payload as SessionResponse;
}

export async function createMicrosoftSession(idToken: string): Promise<SessionResponse> {
  const response = await fetch('/aiapps-auth/session', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      provider: 'azure-ad',
      idToken,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || 'Unable to establish Microsoft session.');
  }

  return payload as SessionResponse;
}

export async function logoutFromMicrosoft(): Promise<void> {
  try {
    await fetch('/aiapps-auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } finally {
    clearAppSession();
  }

  const account = msalInstance.getAllAccounts()[0];

  if (!account) {
    window.location.replace(azureAdConfig.postLogoutRedirectUri);
    return;
  }

  await msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri: azureAdConfig.postLogoutRedirectUri,
  });
}

export async function logoutLocalSession(): Promise<void> {
  try {
    await fetch('/aiapps-auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } finally {
    clearAppSession();
    window.location.replace('/login');
  }
}

export function navigateAfterLogin(): void {
  const redirect = consumePostLoginRedirect();
  const target = redirect
    ? (redirect.startsWith('/') ? redirect : `/${redirect}`)
    : '/apps';

  window.location.replace(target);
}
